import React, { useEffect } from 'react';
import { Box, Button, Container, Grid, Link as MuiLink, Stack, Typography } from '@mui/material';
import { ArrowForward, EventRepeat, Groups, NoteAdd, VerifiedUser } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  BuiltForStrip,
  ChipTone,
  ClosingCta,
  ComparisonTable,
  DEEP,
  Eyebrow,
  FaqItem,
  FaqSection,
  FeatureRow,
  FinePrint,
  GREEN,
  HERO_IMAGE,
  HeroTitle,
  Highlight,
  INK,
  LINE,
  LINE_ON_DARK,
  Lede,
  MINT,
  MOCK_SHADOW,
  MUTED,
  MarketingPage,
  MockCard,
  MockPrimaryLine,
  MockRow,
  PAPER,
  SectionTitle,
  SplitHeading,
  StatusChip,
  faqStructuredData,
  primaryButtonSx,
  secondaryButtonSx,
} from '../components/marketing/MarketingKit';

const toolsIncludedText =
  'You get a complete business management platform: CRM with customer onboarding and multi-property accounts, online e-signature for your own customer agreements, route optimization, a mobile crew app with photos and visit skipping, automated payment processing with weekly payouts, sales tax by state, seasonal contracts paid in full or monthly, next-season renewals, Additional Services estimates for one-time jobs like fall cleanup (flat 1% fee), a Financial Dashboard for revenue and expenses with CSV export, a booked revenue Forecast for the next 30, 60, or 90 days, Property Profit showing revenue per crew-hour for each property, optional Crew Management with time tracking, productivity metrics, and payroll-ready CSV export (no extra SaaS fees), insurance and license reminders, and customer review management. Financial and productivity figures are estimates for informational use only—not accounting or payroll software.';

const FAQS: FaqItem[] = [
  {
    q: 'How much does LawnHub cost?',
    a: 'There is no monthly subscription and nothing to pay up front. LawnHub takes a platform fee on the service price when a job is completed: 1% for customers you bring onto LawnHub, 5% for new marketplace customers during their first season, and 1% on renewals with the same provider. Additional Services are a flat 1%. Customers pay card processing separately, so it is not deducted from your payout.',
  },
  {
    q: 'Can I bring my existing customers?',
    a: 'Yes. Add or import your customers, set their prices and schedules, and invite them to put a card on file. Customers you onboard are charged a 1% platform fee on completed jobs, and you get the same CRM, scheduling, crew, and payment tools you use for marketplace customers.',
  },
  {
    q: 'How and when do I get paid?',
    a: 'The customer is charged automatically when a job is marked complete. Once the payment settles (typically 1–3 days), it is included in your weekly payout to your Stripe Express account. Earnings, fees, and payout status are all visible in your dashboard.',
  },
  {
    q: 'Where do new customer leads come from?',
    a: 'Homeowners in your area request service on LawnHub. You see those requests in your dashboard and submit a bid; the customer compares bids and chooses a provider. There are no lead fees — you only pay the platform fee on jobs you complete.',
  },
  {
    q: 'Can customers sign my service agreement online?',
    a: 'Yes. Upload your agreement as a PDF, send it to one customer or many at once, and customers review and sign it online. The Customer Agreements page shows who has signed, who is still pending, and who has not been sent one yet, and you can resend with one click.',
  },
  {
    q: 'What do Forecast and Property Profit show?',
    a: 'Forecast totals the revenue already booked for the next 30, 60, or 90 days — scheduled visits, seasonal contract payments, and one-time jobs — alongside open estimates and renewals that have not been accepted yet. Property Profit compares what each property earns with the crew time it takes, shows revenue per crew-hour, and flags properties that fall below your target or well below your typical rate.',
  },
  {
    q: 'Do my crews need to download an app?',
    a: 'No. The crew app runs in any phone browser and can be added to the home screen. Crew leaders see their route, mark stops arrived and complete, add before-and-after photos, and skip or reschedule a visit with a reason. The customer is notified automatically.',
  },
  {
    q: 'What is Crew Management?',
    a: 'An optional module with no extra subscription. Add employees with hourly or daily pay rates, let crew leaders start and end routes from the crew app, and see revenue per labor hour, efficiency, and labor cost % by employee or crew. Export a payroll-ready CSV with hours or days worked, rate, and gross pay. Routes and job completion work the same if you skip it. Metrics are estimates for informational use only — not payroll software.',
  },
  {
    q: 'Can I use my own AI assistant with LawnHub?',
    a: 'Yes. It’s open to every provider account as a pilot, which means it works on your real account and we’re still improving it based on provider feedback. In Settings you create a key for your assistant (for example a ChatGPT custom GPT, or another assistant that can connect to outside apps with an API key) and choose what it can see: schedule and routes, customers, or financials. You can also let it move or skip visits, schedule a service type for a day, or add a customer. Every change is previewed and only runs after you say yes, customers get LawnHub’s usual notices, and it can’t move money, change prices, or cancel services. Every request is logged, and you can revoke access at any time. LawnHub doesn’t charge for this and doesn’t run an AI model. Step-by-step setup is in Provider Tutorials.',
  },
  {
    q: 'What tools are included?',
    a: toolsIncludedText,
  },
  {
    q: 'How is this different from other platforms?',
    a: 'Lead marketplaces typically charge per lead or take a large commission and do not help you run the business. Subscription software charges every month whether you are busy or not, and does not bring you customers. LawnHub combines both: full business tools with no subscription, and marketplace leads you only pay for when the work is done.',
  },
];

const RouteMock: React.FC = () => {
  const stops: Array<{ time: string; name: string; street: string; status: ChipTone; label: string }> = [
    { time: '8:10', name: 'Hernandez', street: '4120 Oak Ridge Dr', status: 'green', label: 'Complete' },
    { time: '8:45', name: 'Whitfield', street: '4188 Oak Ridge Dr', status: 'green', label: 'Complete' },
    { time: '9:20', name: 'Okafor', street: '77 Linden Ct', status: 'amber', label: 'On site' },
    { time: '9:55', name: 'Brennan', street: '102 Linden Ct', status: 'grey', label: 'Next' },
    { time: '10:30', name: 'Castillo', street: '5 Meadow Ln', status: 'grey', label: 'Scheduled' },
  ];
  return (
    <MockCard title="Crew 1 · Tuesday route" meta="9 stops · 38 mi">
      {stops.map((s, i) => (
        <MockRow key={s.name} last={i === stops.length - 1}>
          <Typography sx={{ width: 40, fontSize: '0.78rem', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
            {s.time}
          </Typography>
          <MockPrimaryLine title={s.name} subtitle={s.street} />
          <StatusChip tone={s.status}>{s.label}</StatusChip>
        </MockRow>
      ))}
    </MockCard>
  );
};

const HeroVisual: React.FC = () => (
  <Box sx={{ position: 'relative', pb: { xs: 0, md: 6 }, pl: { xs: 0, md: 6 } }}>
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        display: { xs: 'none', md: 'block' },
        width: '78%',
        borderRadius: 4,
        backgroundImage: `url("${HERO_IMAGE}")`,
        backgroundSize: 'cover',
        backgroundPosition: '30% center',
      }}
    />
    <Box sx={{ position: 'relative', pt: { xs: 0, md: 7 }, maxWidth: 380 }}>
      <RouteMock />
    </Box>
    <Box
      aria-hidden
      sx={{
        display: { xs: 'none', md: 'block' },
        position: 'absolute',
        right: 24,
        bottom: 0,
        bgcolor: DEEP,
        color: '#fff',
        borderRadius: 3,
        px: 2.5,
        py: 2,
        boxShadow: MOCK_SHADOW,
        minWidth: 200,
      }}
    >
      <Typography sx={{ fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#b9d8bd' }}>
        Next payout · Friday
      </Typography>
      <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
        $5,822.40
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>61 jobs completed this week</Typography>
    </Box>
  </Box>
);

const AgreementsMock: React.FC = () => {
  const rows: Array<{ name: string; street: string; tone: ChipTone; label: string }> = [
    { name: 'Dana Whitfield', street: '4188 Oak Ridge Dr', tone: 'green', label: 'Signed' },
    { name: 'Marcus Okafor', street: '77 Linden Ct', tone: 'amber', label: 'Awaiting signature' },
    { name: 'Priya Raman', street: '19 Birch Hollow', tone: 'green', label: 'Signed' },
    { name: 'Tom Brennan', street: '102 Linden Ct', tone: 'grey', label: 'Not sent' },
    { name: 'Elena Castillo', street: '5 Meadow Ln', tone: 'grey', label: 'Not sent' },
  ];
  return (
    <MockCard title="Customer agreements" meta="2027 Service Agreement.pdf">
      <Box sx={{ px: 2.25, py: 1.25, bgcolor: MINT, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.8rem', color: DEEP, fontWeight: 600 }}>2 selected</Typography>
        <Box sx={{ bgcolor: GREEN, color: '#fff', fontSize: '0.75rem', fontWeight: 700, px: 1.5, py: 0.6, borderRadius: 1.5 }}>
          Send agreement
        </Box>
      </Box>
      {rows.map((r, i) => (
        <MockRow key={r.name} last={i === rows.length - 1}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.75,
              border: `2px solid ${r.tone === 'grey' ? GREEN : '#c9d0ca'}`,
              bgcolor: r.tone === 'grey' ? GREEN : 'transparent',
              flexShrink: 0,
            }}
          />
          <MockPrimaryLine title={r.name} subtitle={r.street} />
          <StatusChip tone={r.tone}>{r.label}</StatusChip>
        </MockRow>
      ))}
    </MockCard>
  );
};

const CrewAppMock: React.FC = () => (
  <Box
    aria-hidden
    sx={{
      width: 280,
      mx: 'auto',
      bgcolor: '#0f1a12',
      borderRadius: '36px',
      p: 1.25,
      boxShadow: MOCK_SHADOW,
    }}
  >
    <Box sx={{ bgcolor: PAPER, borderRadius: '28px', overflow: 'hidden' }}>
      <Box sx={{ bgcolor: DEEP, color: '#fff', px: 2.5, pt: 3, pb: 2 }}>
        <Typography sx={{ fontSize: '0.72rem', color: '#b9d8bd', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Crew 2 · Today
        </Typography>
        <Typography sx={{ fontSize: '1.15rem', fontWeight: 800 }}>4 of 11 stops done</Typography>
        <Box sx={{ mt: 1.25, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.18)' }}>
          <Box sx={{ width: '36%', height: '100%', borderRadius: 3, bgcolor: '#8bc34a' }} />
        </Box>
      </Box>
      <Box sx={{ p: 1.5, display: 'grid', gap: 1.25 }}>
        <Box sx={{ bgcolor: '#fff', border: `1px solid ${LINE}`, borderRadius: 2.5, p: 1.75 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: INK }}>77 Linden Ct</Typography>
            <StatusChip tone="amber">On site</StatusChip>
          </Stack>
          <Typography sx={{ fontSize: '0.75rem', color: MUTED, mb: 1.5 }}>Mowing · Weekly · Gate code 4411</Typography>
          <Stack direction="row" spacing={1}>
            <Box sx={{ flex: 1, textAlign: 'center', py: 0.9, borderRadius: 1.5, border: `1px solid ${LINE}`, fontSize: '0.75rem', fontWeight: 700, color: INK }}>
              Add photos
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center', py: 0.9, borderRadius: 1.5, bgcolor: GREEN, color: '#fff', fontSize: '0.75rem', fontWeight: 700 }}>
              Complete
            </Box>
          </Stack>
        </Box>
        {[
          { street: '102 Linden Ct', meta: 'Mowing · Weekly' },
          { street: '5 Meadow Ln', meta: 'Mowing + trim · Biweekly' },
        ].map((s) => (
          <Box key={s.street} sx={{ bgcolor: '#fff', border: `1px solid ${LINE}`, borderRadius: 2.5, px: 1.75, py: 1.25 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: INK }}>{s.street}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>{s.meta}</Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize: '0.72rem', color: MUTED, textAlign: 'center', pb: 0.5 }}>
          Skip or reschedule a visit
        </Typography>
      </Box>
    </Box>
  </Box>
);

const PaymentsMock: React.FC = () => {
  const lines: Array<{ label: string; value: string; muted?: boolean; strong?: boolean }> = [
    { label: 'Completed jobs (61)', value: '$5,040.00' },
    { label: 'Seasonal contract installments (8)', value: '$880.00' },
    { label: 'Platform fees', value: '−$97.60', muted: true },
    { label: 'Payout to Stripe · Fri, Oct 2', value: '$5,822.40', strong: true },
  ];
  return (
    <MockCard title="Earnings" meta="Week of Sep 28">
      {lines.map((l, i) => (
        <MockRow key={l.label} last={i === lines.length - 1}>
          <Typography sx={{ flex: 1, fontSize: '0.85rem', color: l.muted ? MUTED : INK, fontWeight: l.strong ? 700 : 400 }}>
            {l.label}
          </Typography>
          <Typography
            sx={{
              fontSize: l.strong ? '1rem' : '0.85rem',
              fontWeight: l.strong ? 800 : 600,
              color: l.strong ? GREEN : l.muted ? MUTED : INK,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {l.value}
          </Typography>
        </MockRow>
      ))}
      <Box sx={{ px: 2.25, py: 1.5, bgcolor: MINT, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {[
          ['Sales tax collected', '$212.35'],
          ['Card processing', 'Paid by customer'],
        ].map(([k, v]) => (
          <Box key={k}>
            <Typography sx={{ fontSize: '0.7rem', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{k}</Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: DEEP }}>{v}</Typography>
          </Box>
        ))}
      </Box>
    </MockCard>
  );
};

const NumbersMock: React.FC = () => {
  const weeks = [62, 70, 58, 74, 81, 66, 49, 38];
  const props: Array<{ street: string; rate: string; tone: ChipTone; label: string }> = [
    { street: '19 Birch Hollow', rate: '$96/hr', tone: 'green', label: 'Healthy' },
    { street: '4120 Oak Ridge Dr', rate: '$81/hr', tone: 'green', label: 'Healthy' },
    { street: '77 Linden Ct', rate: '$44/hr', tone: 'red', label: 'Below target' },
  ];
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <MockCard title="Forecast" meta="Next 60 days">
        <Box sx={{ px: 2.25, pt: 2, pb: 1.5 }}>
          <Typography sx={{ fontSize: '0.72rem', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Booked revenue
          </Typography>
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: INK, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
            $27,940
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 92, mt: 1.5 }}>
            {weeks.map((h, i) => (
              <Box key={i} sx={{ flex: 1, height: `${h}%`, bgcolor: i < 2 ? GREEN : '#a5cfa8', borderRadius: '3px 3px 0 0' }} />
            ))}
          </Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
            <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>Oct 5</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>Nov 23</Typography>
          </Stack>
        </Box>
      </MockCard>
      <MockCard title="Property profit" meta="Revenue per crew-hour">
        {props.map((p, i) => (
          <MockRow key={p.street} last={i === props.length - 1}>
            <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: INK }} noWrap>
              {p.street}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums' }}>
              {p.rate}
            </Typography>
            <StatusChip tone={p.tone}>{p.label}</StatusChip>
          </MockRow>
        ))}
      </MockCard>
    </Box>
  );
};

const ChatBubble: React.FC<{ fromProvider?: boolean; children: React.ReactNode }> = ({ fromProvider, children }) => (
  <Box
    sx={{
      alignSelf: fromProvider ? 'flex-end' : 'flex-start',
      maxWidth: '85%',
      px: 1.75,
      py: 1,
      borderRadius: 2.5,
      fontSize: '0.8rem',
      lineHeight: 1.45,
      bgcolor: fromProvider ? DEEP : MINT,
      color: fromProvider ? '#fff' : INK,
    }}
  >
    {children}
  </Box>
);

const AgentMock: React.FC = () => (
  <MockCard title="Your AI assistant" meta="Connected to LawnHub">
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, bgcolor: PAPER }}>
      <ChatBubble fromProvider>Storm’s coming. Move the rest of today’s stops to Friday.</ChatBubble>
      <Box sx={{ alignSelf: 'flex-start', width: '88%', bgcolor: '#fff', border: `1px solid ${LINE}`, borderRadius: 2.5, p: 1.75 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: MUTED, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Preview
          </Typography>
          <StatusChip tone="amber">Needs your OK</StatusChip>
        </Stack>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: INK, lineHeight: 1.45 }}>
          Move 4 visits from Wed, Oct 7 to Fri, Oct 9. 4 customers will be notified.
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: MUTED, mt: 0.5 }}>Nothing has changed yet.</Typography>
      </Box>
      <ChatBubble fromProvider>Yes, do it.</ChatBubble>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: 'flex-start' }}>
        <StatusChip tone="green">Done</StatusChip>
        <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>4 visits moved · logged in Activity</Typography>
      </Stack>
    </Box>
  </MockCard>
);

const FEES: Array<{ value: string; label: string; note: string }> = [
  { value: '1%', label: 'Your customers', note: 'Customers you bring onto LawnHub' },
  { value: '5%', label: 'New marketplace customers', note: 'First season only' },
  { value: '1%', label: 'Renewals & Additional Services', note: 'Next-season renewals and one-time add-on jobs' },
  { value: '$0', label: 'Monthly subscription', note: 'No setup, seat, or tier fees' },
];

const EXTRAS: Array<{ icon: React.ReactNode; title: string; body: string }> = [
  {
    icon: <NoteAdd fontSize="small" />,
    title: 'Additional Services',
    body: 'Send estimates for cleanups, mulch, aeration, and other one-time work. See which are waiting, accepted, or expired, resend in one click, and schedule accepted jobs like any other. Flat 1%.',
  },
  {
    icon: <EventRepeat fontSize="small" />,
    title: 'Seasonal contracts & renewals',
    body: 'Sell the season up front or in monthly installments billed on the 1st. When the season ends, send renewal offers so customers carry into next year at 1%.',
  },
  {
    icon: <Groups fontSize="small" />,
    title: 'Crew Management',
    body: 'Optional. Set pay rates, clock routes from the crew app, and see revenue per labor hour and labor cost %. Export a payroll-ready CSV for your payroll provider.',
  },
  {
    icon: <VerifiedUser fontSize="small" />,
    title: 'Reviews & compliance',
    body: 'Collect customer reviews on your profile, and get reminders before your insurance or license expires so your account stays in good standing.',
  },
];

const COMPARISON: string[][] = [
  ['Monthly subscription', 'None', 'Often', 'Yes, usually per user or tier'],
  ['Customers you already have', '1% on completed jobs', 'Not supported', 'Included in subscription'],
  ['New customer leads', '5% first season, only when the job is done', 'Pay per lead or a large commission', 'Not included'],
  ['Routes, crew app & payments', 'Included', 'Not included', 'Often on higher tiers'],
  ['Forecast & profit per property', 'Included', 'Not included', 'Varies'],
];

const ProviderBenefitsPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    navigate('/register', { state: { userType: 'provider' } });
  };

  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <SEO
        title="Lawn Care Business Software for Providers | LawnHub"
        description="LawnHub gives lawn care and snow removal providers CRM, e-signed customer agreements, route optimization, a crew app, automatic payments, revenue forecasting, and profit per property — with 1–5% fees and no monthly subscription."
        keywords="lawn care provider software, lawn care CRM, lawn care scheduling software, route optimization, crew management, lawn care job costing, revenue forecast, Jobber alternative, lawn care business tools, snow removal software"
        url="/provider-benefits"
        structuredData={faqStructuredData(FAQS)}
      />

      <MarketingPage>
        {/* Hero */}
        <Box component="section" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 7, md: 10 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Eyebrow>LawnHub for providers</Eyebrow>
                <HeroTitle>
                  Run your lawn care business from <Highlight>one place.</Highlight>
                </HeroTitle>
                <Lede maxWidth={520}>
                  Customers, routes, crews, payments, and the numbers behind them — in one platform built for lawn care
                  and snow removal companies. No monthly subscription.
                </Lede>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                  <Button variant="contained" size="large" onClick={handleGetStarted} endIcon={<ArrowForward />} sx={primaryButtonSx}>
                    Create a free provider account
                  </Button>
                  <Button component={RouterLink} to="/request-demo" variant="outlined" size="large" sx={secondaryButtonSx}>
                    Try the interactive demo
                  </Button>
                </Stack>
                <Typography sx={{ mt: 2.5, fontSize: '0.875rem', color: MUTED }}>
                  Free to join. Fees apply only to completed jobs —{' '}
                  <MuiLink component="button" onClick={scrollToPricing} sx={{ color: GREEN, fontWeight: 600, verticalAlign: 'baseline', fontSize: 'inherit' }}>
                    see pricing
                  </MuiLink>
                  .
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <HeroVisual />
              </Grid>
            </Grid>
          </Container>
        </Box>

        <BuiltForStrip
          label="Built for"
          items={['Owner-operators to 6-crew shops', 'Weekly & biweekly mowing', 'Snow removal', 'Seasonal contracts', 'Licensed & insured pros']}
        />

        <FeatureRow
          eyebrow="Customers & agreements"
          title="Bring your customer list. Get the paperwork signed."
          body="Add the customers you already serve, set their prices and schedules, and invite them to pay online. Upload your own service agreement and send it to everyone who needs it — customers sign on their phone, and you can see exactly who is still outstanding."
          points={[
            'Onboard existing customers at a 1% platform fee',
            'Multi-property customers, each with their own services and schedule',
            'E-sign your own PDF agreement — send to one customer or many at once',
            'Signed, pending, and not-sent status with one-click resend',
          ]}
          visual={<AgreementsMock />}
        />

        <FeatureRow
          reverse
          eyebrow="Routes & crew app"
          title="Plan the day in minutes. Crews run it from their phones."
          body="LawnHub builds each day's route from your shop and groups nearby stops. Crew leaders see their stops, mark arrived and complete, add before-and-after photos — and the customer is charged when the job is done."
          points={[
            'Up to 6 crews, with services assigned per crew',
            'Rearrange routes as the day changes',
            'Skip or reschedule a visit with a reason; the customer is notified and the skip is logged',
            'Runs in the phone browser — nothing to install from an app store',
          ]}
          visual={<CrewAppMock />}
        />

        <FeatureRow
          eyebrow="Payments"
          title="Get paid without chasing anyone."
          body="Cards on file are charged automatically when a job is completed, and your earnings land in your Stripe account every week. Card processing is paid by the customer, so it never comes out of your payout."
          points={[
            'Automatic charges on completion, weekly payouts via Stripe Express',
            'Sales tax by state and property type, added to the customer’s total',
            'Seasonal contracts paid in full or in monthly installments',
            'Every charge, fee, refund, and payout itemized in your dashboard',
          ]}
          visual={<PaymentsMock />}
        />

        <FeatureRow
          reverse
          eyebrow="Financials"
          title="Know what’s coming in, and which properties are worth it."
          body="The Financial Dashboard tracks revenue automatically and lets you log expenses in seconds. Forecast shows revenue already booked for the next 30, 60, or 90 days. Property Profit shows what each property earns per crew-hour, so underpriced accounts stand out before renewal time."
          points={[
            'Revenue, expenses, and estimated profit with CSV export',
            'Booked revenue forecast, plus open estimates and renewals in the pipeline',
            'Revenue per crew-hour by property, with a target you set',
            'Price-increase calculator for flagged properties',
          ]}
          visual={<NumbersMock />}
        />

        <FeatureRow
          eyebrow="AI assistant · Pilot"
          title="Ask your own AI assistant. You stay in charge."
          body="Connect the assistant you already use — like ChatGPT or Muse — to your LawnHub account. Ask what’s left on today’s route, who hasn’t signed their agreement, or what’s booked next month. If you allow it, it can also move or skip visits, put every snow customer on a storm day, or add a new customer. LawnHub doesn’t charge for this and doesn’t run an AI model."
          points={[
            'Every change is shown to you first and only happens after you say yes',
            'You choose what each assistant can see or change — changes stay off until you turn them on',
            'It can’t move money, change prices, or cancel services',
            'Every request is logged in your account, and one click cuts off access',
          ]}
          visual={<AgentMock />}
        />

        {/* Also included */}
        <Box component="section" sx={{ py: { xs: 7, md: 10 }, borderTop: `1px solid ${LINE}` }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 2, md: 6 }} sx={{ mb: { xs: 4, md: 6 } }}>
              <Grid item xs={12} md={5}>
                <Eyebrow>Also included</Eyebrow>
                <SectionTitle>The rest of the business, handled.</SectionTitle>
              </Grid>
              <Grid item xs={12} md={7} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Lede>No add-on pricing and no upgrade tiers. Every provider account includes all of it.</Lede>
              </Grid>
            </Grid>
            <Grid container spacing={{ xs: 4, md: 5 }}>
              {EXTRAS.map((e) => (
                <Grid item xs={12} sm={6} md={3} key={e.title}>
                  <Box sx={{ borderTop: `2px solid ${INK}`, pt: 2.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: MINT, color: GREEN, display: 'grid', placeItems: 'center', mb: 2 }}>
                      {e.icon}
                    </Box>
                    <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 1, color: INK }}>
                      {e.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.925rem', lineHeight: 1.6, color: MUTED }}>{e.body}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Pricing */}
        <Box id="pricing" component="section" sx={{ bgcolor: DEEP, color: '#fff', py: { xs: 8, md: 11 }, scrollMarginTop: 80 }}>
          <Container maxWidth="lg">
            <SplitHeading
              light
              eyebrow="Pricing"
              title="Pricing you can explain in one sentence."
              body="You pay a small platform fee on the service price when a job is completed. That’s it — nothing monthly, nothing up front, nothing when it’s slow."
            />
            <Grid container sx={{ borderTop: `1px solid ${LINE_ON_DARK}` }}>
              {FEES.map((f, i) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={f.label}
                  sx={{
                    py: { xs: 3, md: 4 },
                    pr: { md: 3 },
                    pl: { md: i === 0 ? 0 : 3 },
                    borderLeft: { md: i === 0 ? 'none' : `1px solid ${LINE_ON_DARK}` },
                    borderBottom: { xs: `1px solid ${LINE_ON_DARK}`, md: 'none' },
                  }}
                >
                  <Typography sx={{ fontSize: { xs: '3rem', md: '3.75rem' }, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                    {f.value}
                  </Typography>
                  <Typography sx={{ mt: 1.5, fontWeight: 700, fontSize: '1rem' }}>{f.label}</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.65)' }}>{f.note}</Typography>
                </Grid>
              ))}
            </Grid>
            <Typography sx={{ mt: { xs: 4, md: 5 }, fontSize: '0.85rem', color: 'rgba(255,255,255,0.65)', maxWidth: 760 }}>
              Card processing is paid by the customer and is not deducted from your payout. Renewal pricing applies when a
              marketplace customer renews with the same provider.
            </Typography>
          </Container>
        </Box>

        {/* Comparison */}
        <Box component="section" sx={{ py: { xs: 7, md: 11 } }}>
          <Container maxWidth="lg">
            <Eyebrow>How it compares</Eyebrow>
            <SectionTitle>Business tools and new customers, without the subscription.</SectionTitle>
            <Lede maxWidth={640}>
              Most providers end up paying for two things: software to run the business and a marketplace to find customers.
              LawnHub is both.
            </Lede>
            <ComparisonTable columns={['', 'LawnHub', 'Typical lead marketplace', 'Typical subscription software']} rows={COMPARISON} />
          </Container>
        </Box>

        <FaqSection
          title="Questions providers ask."
          faqs={FAQS}
          aside={
            <>
              Want to see it with your own routes and customers?{' '}
              <MuiLink component={RouterLink} to="/request-demo" sx={{ color: GREEN, fontWeight: 600 }}>
                Request a demo
              </MuiLink>
              .
            </>
          }
        />

        <ClosingCta
          title="Set up your business this week."
          body="Create your free account, add your services and customers, and run your first route. Or walk through the demo first — no account needed."
          primary={{ label: 'Create a free provider account', onClick: handleGetStarted }}
          secondary={{ label: 'Try the demo', to: '/request-demo' }}
        />

        <FinePrint>
          Product previews on this page use sample data. Financial Dashboard, Forecast, Property Profit, and Crew
          Management figures are estimates for informational purposes only and are not accounting, tax, or payroll
          advice. You are responsible for your own bookkeeping, sales tax remittance, and payroll. See our{' '}
          <MuiLink component={RouterLink} to="/legal" sx={{ color: GREEN }}>
            legal terms
          </MuiLink>{' '}
          or{' '}
          <MuiLink component={RouterLink} to="/about-us" sx={{ color: GREEN }}>
            learn more about LawnHub
          </MuiLink>
          .
        </FinePrint>
      </MarketingPage>
    </>
  );
};

export default ProviderBenefitsPage;
